import { CmsMeta } from '../cmsMeta';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Link } from 'react-router-dom';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { Img } from '../../../components/Img/Img';
import { Loader } from '../../../components/Loader/Loader';
import SanitizedHtml from '../../../components/SanitizedHtml/SanitizedHtml';
import { useConfig } from '../../../config';
import { useLoop54ProductsByIds } from '../../../hooks/useLoop54ProductsByIds';
import { useTranslation } from '../../../i18n/i18n';
import { ApiProduct, CustomerType } from '../../../types';
import { sortByIdOrder } from '../../utils';
import './CmsSuperBanner.css';

type SuperBannerClickProps = {
	savedComponentId?: string | number;
	savedComponentTitle?: string;
};

type SuperBannerProductClickProps = SuperBannerClickProps & {
	productId?: string | number;
};

const trackSuperBannerClick = (props: SuperBannerClickProps) => {
	if (typeof window === 'undefined' || typeof window.plausible === 'undefined') return;

	window.plausible('Super Banner Click', {
		props,
	});
};

const trackSuperBannerProductClick = (props: SuperBannerProductClickProps) => {
	if (typeof window === 'undefined' || typeof window.plausible === 'undefined') return;

	window.plausible('Super Banner Product Click', {
		props,
	});
};

export const BannerProduct = ({
	product,
	savedComponentId,
	savedComponentTitle,
}: { product: ApiProduct } & SuperBannerClickProps): JSX.Element => {
	const size = { width: 292, height: 292 };
	return (
		<div className="banner-product">
			<Link
				to={'/' + product.perma}
				onClick={() =>
					trackSuperBannerProductClick({
						savedComponentId,
						savedComponentTitle,
						productId: product.id,
					})
				}
			>
				<Img alt={product.name} src={product.image} {...size} imgHeight={100} imgWidth={100} />
			</Link>
		</div>
	);
};

export const BannerIcon = ({ imageUrl, text }: { imageUrl: string; text: string }): JSX.Element => {
	return (
		<div className="banner-icon">
			<div>
				<img alt={text} src={imageUrl} width={38} height={38} />
				{text}
			</div>
		</div>
	);
};

export const BannerProducts = ({
	productIds,
	limit,
	savedComponentId,
	savedComponentTitle,
}: { productIds: number[]; limit?: number } & SuperBannerClickProps): JSX.Element => {
	const loader = (
		<>
			<Loader size="line" />
			<Loader size="line" />
			<Loader size="line" />
			<Loader size="line" />
		</>
	);
	const { error, data } = useLoop54ProductsByIds(productIds);
	if (productIds.length === 0) return loader;
	if (error) return <ErrorMessage />;
	if (!data) return loader;
	if (!data.results.items || data.results.items.length === 0) return <ErrorMessage />;

	const sortedProducts = sortByIdOrder(data.results.items, productIds);

	return (
		<>
			{sortedProducts.slice(0, limit).map((p) => (
				<BannerProduct
					product={p}
					key={p.id}
					savedComponentId={savedComponentId}
					savedComponentTitle={savedComponentTitle}
				/>
			))}
		</>
	);
};

type CmsSuperBannerParams = {
	img: string;
	content: string;
	productIds: number[];
	ctaLink: string;
	ctaText: string;
	backgroundColor: string;
	worky?: boolean;
	customerType?: 'company' | 'private';
	badgeText?: string;
	badgeColor?: string;
	logo?: string;
	startDate?: string;
	endDate?: string;
	savedComponentId?: string | number;
	savedComponentTitle?: string;
};

const lightToDarkCategoryColorMap: Record<string, string> = {
	'#e1f4ff': '#73cbff', // category 1
	'#e8eefe': '#8cabff', // category 2
	'#ede8fe': '#b298fe', // category 3
	'#fae8fe': '#e988fc', // category 4
	'#fee8ef': '#ff80a6', // category 5
	'#fee0d4': '#ff956b', // category 6
	'#fdf1dc': '#ffc766', // category 7
	'#fed4d4': '#a01515', // category 8
	'#ffee58': '#6c620b', // yellow
	'#fbf5c6': '#ffee58', // light-yellow
	'#fffbdc': '#ffee58', // light-yellow3
	'#faf6cb': '#ffee58', // yellow 4
	'#78e989': '#1e3a22', // green
	'#e4fbe7': '#78e989', // light-green
};

const parseScheduleDate = (dateValue?: string, isEndDate = false): number | null => {
	const value = dateValue?.trim();
	if (!value) return null;

	const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (dateOnlyMatch) {
		const [, year, month, day] = dateOnlyMatch;
		const yearNum = Number(year);
		const monthNum = Number(month);
		const dayNum = Number(day);
		const parsed = new Date(yearNum, monthNum - 1, dayNum);
		if (Number.isNaN(parsed.getTime())) return null;
		if (parsed.getFullYear() !== yearNum || parsed.getMonth() !== monthNum - 1 || parsed.getDate() !== dayNum) {
			return null;
		}

		if (isEndDate) {
			parsed.setHours(23, 59, 59, 999);
		} else {
			parsed.setHours(0, 0, 0, 0);
		}

		return parsed.getTime();
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return null;

	return parsed.getTime();
};

const isScheduledBannerVisible = (startDate?: string, endDate?: string): boolean => {
	const startTs = parseScheduleDate(startDate);
	const endTs = parseScheduleDate(endDate, true);
	const now = Date.now();

	if (startDate?.trim() && startTs === null) return true;
	if (endDate?.trim() && endTs === null) return true;
	if (startTs !== null && now < startTs) return false;
	if (endTs !== null && now > endTs) return false;

	return true;
};

export const CmsSuperBanner = ({
	img,
	content,
	productIds,
	worky,
	ctaLink,
	ctaText,
	backgroundColor = '#fee8fd',
	badgeText = '',
	badgeColor,
	logo,
	startDate,
	endDate,
	savedComponentId,
	savedComponentTitle,
}: CmsSuperBannerParams): JSX.Element | null => {
	if (!isScheduledBannerVisible(startDate, endDate)) return null;
	const normalizedImageSrc = img?.trim();

	const colorMap = (bgColor: string): string => {
		if (!bgColor) return bgColor;

		const normalized = bgColor.trim().toLowerCase();

		// Normalize to 7-char hex (#rrggbb) if possible
		const hexWithHash =
			normalized.startsWith('#') && normalized.length === 7
				? normalized
				: normalized.match(/^[0-9a-f]{6}$/)
					? `#${normalized}`
					: normalized;

		return lightToDarkCategoryColorMap[hexWithHash] || bgColor;
	};

	const badgeBackgroundColor = badgeColor ? colorMap(badgeColor) : colorMap(backgroundColor);
	const normalizedBadgeBackground = badgeBackgroundColor?.trim().toLowerCase();
	const isWhiteTextBackground = normalizedBadgeBackground === '#a01515';

	const badgeHtml = badgeText
		? `<div class="carousel-badge" style="background-color: ${badgeBackgroundColor}${
				isWhiteTextBackground ? '; color: #ffffff' : ''
			}">${badgeText}</div>`
		: '';

	return (
		<div className="super-banner">
			<SanitizedHtml
				className={`content ${badgeHtml ? 'with-badge' : ''}`}
				style={{
					backgroundColor: backgroundColor,
				}}
				html={badgeHtml + content}
			/>
			<div className="image">
				{normalizedImageSrc ? (
					<img
						src={normalizedImageSrc}
						alt=""
						className="banner-image"
						fetchpriority="high"
						loading="eager"
					/>
				) : null}
				{logo && (
					<div className="logo-wrapper">
						<img src={logo} alt="" className="logo" />
					</div>
				)}
			</div>
			<div className="thirdBox">
				<div className="products">
					{worky ? (
						<>
							<BannerIcon imageUrl="/images/apple-logo.svg" text="Fruktkorg"></BannerIcon>
							<BannerIcon imageUrl="/images/coffee2.svg" text="Kaffe & Vatten"></BannerIcon>
							<BannerIcon imageUrl="/images/wrench.svg" text="Handyman"></BannerIcon>
							<BannerIcon imageUrl="/images/magic-wand.svg" text="Kontorsstäd"></BannerIcon>
						</>
					) : (
						<BannerProducts
							limit={4}
							productIds={productIds}
							savedComponentId={savedComponentId}
							savedComponentTitle={savedComponentTitle}
						></BannerProducts>
					)}
				</div>
				<Link
					className="button"
					to={ctaLink}
					onClick={() => trackSuperBannerClick({ savedComponentId, savedComponentTitle })}
				>
					{ctaText}
				</Link>
			</div>
		</div>
	);
};

type CmsOndioSpecialBannerParams = CmsSuperBannerParams & {
	middleContent: string;
	videoUrl: string;
};

export const CmsOndioSpecialBanner = ({
	content,
	ctaLink,
	ctaText,
	backgroundColor = '#fee8fd',
	badgeText = '',
	middleContent,
	videoUrl,
	startDate,
	endDate,
}: CmsOndioSpecialBannerParams): JSX.Element | null => {
	if (!isScheduledBannerVisible(startDate, endDate)) return null;

	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const videoEl = videoRef.current;

		if (!videoEl) return;
		videoEl.muted = true;
		videoEl.defaultMuted = true;

		return () => {};
	}, []);

	const { cmsImage } = useConfig();
	const normalizedBackground = backgroundColor?.trim().toLowerCase();
	const isWhiteTextBackground = normalizedBackground === '#fed4d4' || normalizedBackground === 'fed4d4';

	const colorMap = (bgColor: string): string => {
		if (!bgColor) return bgColor;

		const normalized = bgColor.trim().toLowerCase();

		// Normalize to 7-char hex (#rrggbb) if possible
		const hexWithHash =
			normalized.startsWith('#') && normalized.length === 7
				? normalized
				: normalized.match(/^[0-9a-f]{6}$/)
					? `#${normalized}`
					: normalized;

		return lightToDarkCategoryColorMap[hexWithHash] || bgColor;
	};

	const badgeHtml = badgeText
		? `<div class="carousel-badge" style="background-color: ${colorMap(backgroundColor)}${
				isWhiteTextBackground ? '; color: #ffffff' : ''
			}">${badgeText}</div>`
		: '';

	const getSafeVideoUrl = (url: string): string => {
		if (!url || !cmsImage) return '';

		try {
			// const configuredBase = new URL(cmsImage);
			const resolvedUrl = new URL(url, cmsImage);

			return resolvedUrl.toString();

			// skip comparison for now

			// if (resolvedUrl.origin !== configuredBase.origin) return '';
			// return resolvedUrl.toString();
		} catch {
			return '';
		}
	};

	const safeVideoUrl = getSafeVideoUrl(videoUrl);

	return (
		<div className="super-banner video-banner">
			<SanitizedHtml
				className={`content ${badgeHtml ? 'with-badge' : ''}`}
				style={{
					backgroundColor: backgroundColor,
				}}
				html={badgeHtml + content}
			/>
			<div className="image">
				<SanitizedHtml
					className="content"
					style={{
						backgroundColor: backgroundColor,
					}}
					html={middleContent}
				/>
			</div>
			<div className="thirdBox">
				<div className="video">
					{safeVideoUrl && (
						<div
							style={{
								gridColumn: '1 / -1',
								gridRow: '1 / -1',
								borderRadius: 'var(--border-radius-4)',
								overflow: 'hidden',
								height: '100%',
							}}
						>
							<video
								ref={videoRef}
								autoPlay
								muted={true}
								loop
								playsInline
								preload="metadata"
								onLoadedData={(event) => {
									void event.currentTarget.play().catch(() => undefined);
								}}
								style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
							>
								<source src={safeVideoUrl} />
							</video>
						</div>
					)}
				</div>
				<Link className="button" to={ctaLink}>
					{ctaText}
				</Link>
			</div>
		</div>
	);
};

type CmsSuperBannerCarouselParams = {
	children?: React.ReactNode;
};

export const CmsSuperBannerCarousel = ({ children }: CmsSuperBannerCarouselParams) => {
	const [isFirstRender, setIsFirstRender] = useState(true);
	// const [customerTypeSelected, setCustomerTypeSelected] = useState(false);
	const customerTypeCookie = 'private';
	const interval = 7000;
	const timePadding = 1500;
	let carouselRef = useRef<Carousel>(null);
	const t = useTranslation().carousel;
	// the arrows render even with showArrows={false}, so their labels always need translating
	const carouselLabels = {
		leftArrow: t.previousSlide,
		rightArrow: t.nextSlide,
		item: t.slideItem,
	};

	function filterChildren(children: React.ReactNode, customerType: CustomerType) {
		return React.Children.toArray(children).filter((child): child is React.ReactElement => {
			if (!React.isValidElement(child)) return false;
			const attributes = child.props;

			if (!attributes) return false;
			if (!isScheduledBannerVisible(attributes.startDate, attributes.endDate)) return false;
			if (!attributes.customerType) return true;

			return attributes.customerType === customerType;
		});
	}

	const renderedChildren = useMemo(() => filterChildren(children, customerTypeCookie), [children]);
	const childrenLength = renderedChildren.length;
	const moveToFirstSlideInterval = childrenLength * interval + timePadding;

	if (childrenLength === 0) return null;

	useEffect(() => {
		const interval = setInterval(() => {
			if (carouselRef.current) carouselRef.current.moveTo(0);
		}, moveToFirstSlideInterval);
		return () => clearInterval(interval);
	}, [moveToFirstSlideInterval]);

	useEffect(() => {
		if (isFirstRender) setIsFirstRender(false);
	}, []);

	// useEffect(() => {
	// if (!customerTypeSelected) {
	// setRenderedChildren(filterChildren(children, 'private'));
	// setCustomerTypeSelected(true);
	// }
	// }, []);

	const bannerSkeleton = [
		<CmsSuperBanner
			backgroundColor="#f5f5f5"
			content=""
			ctaLink=""
			ctaText="&nbsp;"
			img=""
			productIds={[]}
			key="skeleton-banner-1"
		/>,
		...Array(childrenLength)
			.fill(null)
			.map((_, i) => <React.Fragment key={`skeleton-padding-banner-${i}`} />),
	];

	if (isFirstRender) {
		// 'swipeable' prop needs to be *absent* on first render 'cause weird animation glitch
		return (
			<Carousel
				interval={interval}
				showThumbs={false}
				showArrows={false}
				autoPlay
				showStatus={false}
				infiniteLoop={false}
				labels={carouselLabels}
				ref={carouselRef}
			>
				{bannerSkeleton}
			</Carousel>
		);
	}

	return (
		<Carousel
			interval={interval}
			showThumbs={false}
			swipeable={false}
			showArrows={false}
			autoPlay
			showStatus={false}
			infiniteLoop={false}
			labels={carouselLabels}
			ref={carouselRef}
		>
			{renderedChildren}
		</Carousel>
	);
};

export const cmsMetas: Record<string, CmsMeta> = {
	CmsSuperBanner: {
		description:
			'The big banner on a front page: a picture, a headline, one link and a row of products. `content` is html, so the heading goes in an h2 inside it. Put it inside a CmsSuperBannerCarousel unless it is the only one.',
		example: [
			'CmsSuperBanner',
			{
				index: 1,
				img: 'https://cdn.ondio.com/assets/99f9ecf4d1bc4e24bd32464849a77ded.jpg',
				logo: '',
				badgeText: 'Back to work',
				content: '<h2>Jetzt durchstarten!</h2><p>Entdecken Sie alles für ergonomisches Arbeiten.</p>',
				ctaText: 'Angebote ansehen →',
				ctaLink: '/aktionsangebote',
				backgroundColor: '#fbf5c6',
				productIds: ['80017', '82188', '78657', '75396'],
			},
		],
	},
	CmsSuperBannerCarousel: {
		description: 'Holds several CmsSuperBanners and rotates through them. One banner alone does not need it.',
		example: [
			'CmsSuperBannerCarousel',
			{ index: 0 },
			[
				'CmsSuperBanner',
				{
					index: 1,
					img: 'https://cdn.ondio.com/assets/99f9ecf4d1bc4e24bd32464849a77ded.jpg',
					logo: '',
					badgeText: 'Back to work',
					content: '<h2>Jetzt durchstarten!</h2><p>Entdecken Sie alles für ergonomisches Arbeiten.</p>',
					ctaText: 'Angebote ansehen →',
					ctaLink: '/aktionsangebote',
					backgroundColor: '#fbf5c6',
					productIds: ['80017', '82188', '78657', '75396'],
				},
			],
		],
	},
	CmsOndioSpecialBanner: {
		description:
			'The wide banner built for the rename. `middleContent` is required and is the whole central panel - leave it out and the middle of the banner is blank. Written for that campaign rather than for general use, so reach for CmsSuperBanner first.',
		example: [
			'CmsOndioSpecialBanner',
			{
				index: 2,
				badgeText: 'Navneskift',
				content: '<h2>Worky hedder nu Ondio</h2><div>Ét navn i Europa. Stærkere i Danmark.</div>',
				middleContent:
					'<h1 style="display: inline-block; margin: 6px 0; padding: 6px 9px; border-radius: 8px; font-size: 32px; font-family: Mabry Pro Medium; background-color: #95e693;">Bedste kunde</h1><br>',
				ctaText: 'Worky bliver Ondio →',
				ctaLink: '/navneskift',
				videoUrl: 'https://cdn.ondio.com/assets/e55361a3fcfb435db133767201384ccc.mp4',
				backgroundColor: '#e4fbe7',
			},
		],
	},
};
